import React, { useState, useEffect, useRef } from 'react';
import { Button, Tooltip } from 'antd';
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';

export const StatusMetricGroup = ({ title, onExport, exportLoading, children, testId, totalCount }) => {
  const containerRef = useRef(null);
  const cardsContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  // Measure container width and update on resize
  useEffect(() => {
    const measureContainer = () => {
      if (cardsContainerRef.current) {
        const rect = cardsContainerRef.current.getBoundingClientRect();
        const newWidth = rect.width;
        
        
        // Only update if width actually changed to prevent unnecessary re-renders
        if (Math.abs(newWidth - containerWidth) > 1) {
          setContainerWidth(newWidth);
        }
      }
      setScreenWidth(window.innerWidth);
    };

    // Initial measurement with delay to ensure DOM is ready
    const initialMeasure = () => {
      setTimeout(measureContainer, 0);
    };
    initialMeasure();

    // Set up resize observer for more accurate container width tracking
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        
        if (Math.abs(newWidth - containerWidth) > 1) {
          setContainerWidth(newWidth);
        }
      }
    });

    if (cardsContainerRef.current) {
      resizeObserver.observe(cardsContainerRef.current);
    }

    // Fallback window resize listener with throttling
    let resizeTimeout;
    const handleWindowResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(measureContainer, 100);
    };
    
    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
      clearTimeout(resizeTimeout);
    };
  }, [containerWidth, title]);
  
  // Calculate smart gap based on available space
  const getSmartGap = () => {
    if (screenWidth < 768) {
      return 8; // Smaller gap on mobile to fit more
    } else if (screenWidth < 1200) {
      return 6; // Medium gap on tablet
    } else {
      return 4; // Minimal gap on desktop to maximize card space
    }
  };
  
  // Calculate responsive font sizes
  const getFontSizes = () => {
    if (screenWidth < 768) {
      return { title: '18px', total: '18px' }; // Larger fonts on mobile
    } else if (screenWidth < 1200) {
      return { title: '16px', total: '16px' }; // Medium fonts on tablet
    } else if (screenWidth < 1600) {
      return { title: '14px', total: '14px' }; // Smaller fonts on small desktop
    } else {
      return { title: '16px', total: '16px' }; // Optimal fonts on large desktop
    }
  };
  
  const gap = getSmartGap();
  const fontSizes = getFontSizes();
  const totalCards = React.Children.count(children);

  // Clone children and pass container width and card count
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        containerWidth,
        totalCards,
        gap
      });
    }
    return child;
  });
  return (
    <section 
      data-testid={testId} 
      style={{ 
        borderRadius: '12px', 
        border: '2px solid #001529', // Crisis Compass blue (same as sidebar) - DOUBLED THICKNESS
        backgroundColor: '#fff', 
        padding: '11px', // Further reduced by 10% (was 12px) 
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // Subtle shadow
        flex: '1',
        minWidth: '0', // Allow flex shrinking
        height: '100%', // Ensure consistent height
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box' // Include padding/border in height calculations
      }}
    >
      <div style={{ marginBottom: '5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}> {/* Reduced from 6px */}
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}> {/* Made bold, reduced font size */}
          {title}
          <Tooltip title={`${title} overview`}>
            <InfoCircleOutlined style={{ marginLeft: '8px', color: '#999', fontSize: '12px' }} />
          </Tooltip>
        </h3>
        {onExport && (
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={onExport}
            loading={exportLoading}
            style={{
              backgroundColor: '#001529', // Crisis Compass blue
              borderColor: '#001529',
              color: '#ffffff' // White text
            }}
          >
            Export
          </Button>
        )}
      </div>
      {/* Total count below title */}
      <div style={{ 
        marginBottom: '11px', // Further reduced by 10% (was 12px)
        fontSize: '14px', // Reduced from 16px  
        fontWeight: '700', // Same style as title
        color: '#001529' // Crisis Compass blue
      }}>
        Total: {totalCount || 0}
      </div>
      <div 
        ref={cardsContainerRef}
        style={{ 
          display: 'flex', 
          gap: `${gap}px`, 
          flexWrap: 'nowrap', // Always keep single row, let cards shrink instead
          alignItems: 'stretch', // Ensure consistent height
          overflow: 'hidden', // Prevent any overflow
          flex: '1', // Take up remaining space in the container
          minHeight: '80px', // Ensure minimum content height for consistency
          width: '100%', // Ensure container takes full width
          boxSizing: 'border-box' // Include padding/border in width calculations
        }}
      >
        {enhancedChildren}
      </div>
    </section>
  );
};
